import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { useIsMobile } from "../../hooks/useMediaQuery";
import useReaderStore from "../../context/readerStore";
import { apiFormatToStroke } from "../../services/drawingService";
import { generatePageThumbnail } from "../../services/pdfService";
import "./PagesThumbnails.css";

const PagesThumbnails = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    document: pdfDocument,
    totalPages,
    currentPage,
    setCurrentPage,
    documentType,
    togglePagesSidebar,
    loadedChunks,
    isChunked,
    metadata,
    loadChunk,
  } = useReaderStore();
  const highlightsFromStore = useReaderStore(
    (s) => s.annotations.highlights.data,
  );
  const underlinesFromStore = useReaderStore(
    (s) => s.annotations.underlines.data,
  );
  const drawingsFromStore = useReaderStore(
    (s) => s.annotations.freehand_drawing?.data || [],
  );

  // Map absolute page number -> list of highlight rects { left, top, width, height, color } for thumbnails
  const highlightsByPage = useMemo(() => {
    const map = {};
    if (!Array.isArray(highlightsFromStore)) return map;
    highlightsFromStore.forEach((hl) => {
      const page = hl.page_number || 1;
      if (!map[page]) map[page] = [];
      const coords = hl.coordinates;
      const coordsArray = Array.isArray(coords)
        ? coords
        : coords
          ? [coords]
          : [
              {
                left: hl.left ?? 0,
                top: hl.top ?? 0,
                width: hl.width ?? 0,
                height: hl.height ?? 0,
              },
            ];
      coordsArray.forEach((coord) => {
        map[page].push({
          left: coord.left ?? 0,
          top: coord.top ?? 0,
          width: coord.width ?? 0,
          height: coord.height ?? 0,
          color: hl.color,
        });
      });
    });
    return map;
  }, [highlightsFromStore]);

  const underlinesByPage = useMemo(() => {
    const map = {};
    if (!Array.isArray(underlinesFromStore)) return map;

    underlinesFromStore.forEach((underline) => {
      const page = underline.page_number || 1;
      if (!map[page]) map[page] = [];

      const coords = underline.coordinates;
      const coordsArray = Array.isArray(coords)
        ? coords
        : coords
          ? [coords]
          : [underline];

      coordsArray.forEach((coord) => {
        map[page].push({
          left: coord.left ?? 0,
          top: coord.top ?? 0,
          width: coord.width ?? 0,
          height: coord.height ?? 0,
          color: underline.color,
        });
      });
    });

    return map;
  }, [underlinesFromStore]);

  const drawingsByPage = useMemo(() => {
    const map = {};
    if (!Array.isArray(drawingsFromStore)) return map;

    drawingsFromStore.forEach((drawing) => {
      try {
        const stroke = drawing?.points ? drawing : apiFormatToStroke(drawing);
        if (!stroke?.points?.length) return;
        const page = stroke.page_number || drawing.page_number || 1;
        if (!map[page]) map[page] = [];
        map[page].push(stroke);
      } catch (error) {
        console.error("Error processing thumbnail drawing:", error);
      }
    });

    return map;
  }, [drawingsFromStore]);

  const annotationSignatureByPage = useMemo(() => {
    const signatures = {};
    const pageNumbers = new Set([
      ...Object.keys(highlightsByPage),
      ...Object.keys(underlinesByPage),
      ...Object.keys(drawingsByPage),
    ]);

    pageNumbers.forEach((pageKey) => {
      const page = Number(pageKey);
      const pageHighlights = highlightsByPage[page] || [];
      const pageUnderlines = underlinesByPage[page] || [];
      const pageDrawings = drawingsByPage[page] || [];

      signatures[page] = JSON.stringify({
        highlights: pageHighlights.map((item) => [
          item.left ?? 0,
          item.top ?? 0,
          item.width ?? 0,
          item.height ?? 0,
          item.color || "",
        ]),
        underlines: pageUnderlines.map((item) => [
          item.left ?? 0,
          item.top ?? 0,
          item.width ?? 0,
          item.height ?? 0,
          item.color || "",
        ]),
        drawings: pageDrawings.map((stroke) => [
          stroke.id || "",
          stroke.color || "",
          stroke.lineWidth ?? 1,
          stroke.points?.length || 0,
          stroke.points?.[0]?.x ?? 0,
          stroke.points?.[0]?.y ?? 0,
          stroke.points?.[stroke.points.length - 1]?.x ?? 0,
          stroke.points?.[stroke.points.length - 1]?.y ?? 0,
        ]),
      });
    });

    return signatures;
  }, [drawingsByPage, highlightsByPage, underlinesByPage]);

  // State for UI rendering
  const [thumbnails, setThumbnails] = useState({});
  const [loadingPages, setLoadingPages] = useState(new Set());
  // Refs for logic (source of truth for async worker)
  const queueRef = useRef(new Set());
  const loadingPagesRef = useRef(new Set());
  const visiblePagesRef = useRef(new Set());
  const processingRef = useRef(false);
  const mountedRef = useRef(true);
  const containerRef = useRef(null);
  const itemsRef = useRef(new Map());
  const thumbnailCacheRef = useRef({});
  const observerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // -- Helper: Resolve correct document and page number for chunks --
  const getDocumentForPage = useCallback(
    (pageNumber) => {
      if (!isChunked || !metadata) return pdfDocument;

      // Chunk index is 0-based (chunk size = 5)
      const chunkSize = 5;
      const chunkIndex = Math.floor((pageNumber - 1) / chunkSize);

      // Return the specific chunk document if loaded, otherwise null
      return loadedChunks[chunkIndex] || null;
    },
    [isChunked, metadata, loadedChunks, pdfDocument],
  );

  const getRelativePageNumber = useCallback(
    (pageNumber) => {
      if (!isChunked || !metadata) return pageNumber;
      // Relative page is 1-based index within the chunk (chunk size = 5)
      const chunkSize = 5;
      return ((pageNumber - 1) % chunkSize) + 1;
    },
    [isChunked, metadata],
  );

  // -- Worker Logic --
  const processNext = useCallback(async () => {
    if (processingRef.current || !mountedRef.current) return;

    // 1. Get next page from queue (FIFO)
    let nextPage = null;
    for (const page of queueRef.current) {
      nextPage = page;
      break;
    }

    if (!nextPage) return; // Queue empty

    // 2. Mark as processing
    processingRef.current = true;
    queueRef.current.delete(nextPage);

    // 3. Check cache
    if (thumbnailCacheRef.current[nextPage]) {
      setThumbnails((prev) => ({
        ...prev,
        [nextPage]: thumbnailCacheRef.current[nextPage],
      }));
      processingRef.current = false;
      setTimeout(processNext, 5);
      return;
    }

    // Check if already loading (prevent duplicate requests)
    if (loadingPagesRef.current.has(nextPage)) {
      processingRef.current = false;
      setTimeout(processNext, 5);
      return;
    }

    // 4. Resolve Document & Page
    let targetDoc = getDocumentForPage(nextPage);
    const relativePage = getRelativePageNumber(nextPage);

    // If chunk not loaded yet, try to load it (Active Loading)
    if (!targetDoc && isChunked && metadata) {
      try {
        const chunkSize = 5;
        const chunkIndex = Math.floor((nextPage - 1) / chunkSize);
        // This will trigger a fetch if not already loading/loaded
        // loadChunk returns the chunk document promise
        targetDoc = await loadChunk(chunkIndex);
      } catch (err) {
        // Failed to load chunk
        processingRef.current = false;
        setTimeout(processNext, 50); // Retry later?
        return;
      }
    }

    if (!targetDoc) {
      // Still no doc (maybe load failed or not chunked mode issue)
      processingRef.current = false;
      setTimeout(processNext, 5);
      return;
    }

    // 5. Load
    loadingPagesRef.current.add(nextPage);
    setLoadingPages(new Set(loadingPagesRef.current)); // Update UI

    try {
      const pageHighlights = highlightsByPage[nextPage] || [];
      const pageUnderlines = underlinesByPage[nextPage] || [];
      const pageDrawings = drawingsByPage[nextPage] || [];
      const dataURL = await generatePageThumbnail(
        targetDoc,
        relativePage,
        0.5,
        pageHighlights,
        pageUnderlines,
        pageDrawings,
      );

      if (mountedRef.current) {
        thumbnailCacheRef.current[nextPage] = dataURL;
        setThumbnails((prev) => ({ ...prev, [nextPage]: dataURL }));
      }
    } catch (error) {
      // Silently ignore cancellation errors, log others
      if (error?.name !== "RenderingCancelledException") {
        // console.warn(`Thumbnail generation failed for page ${nextPage}:`, error);
      }
    } finally {
      if (mountedRef.current) {
        loadingPagesRef.current.delete(nextPage);
        setLoadingPages(new Set(loadingPagesRef.current)); // Update UI

        processingRef.current = false;
        // Process next item
        setTimeout(processNext, 10);
      }
    }
  }, [
    getDocumentForPage,
    getRelativePageNumber,
    drawingsByPage,
    highlightsByPage,
    isChunked,
    loadChunk,
    metadata,
    underlinesByPage,
  ]);

  // Trigger processing
  const triggerProcessing = useCallback(() => {
    if (!processingRef.current) {
      processNext();
    }
  }, [processNext]);

  // -- Intersection Observer --
  useEffect(() => {
    if (!containerRef.current) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let addedToQueue = false;

        entries.forEach((entry) => {
          const pageNumber = parseInt(
            entry.target.getAttribute("data-page-number"),
            10,
          );

          if (entry.isIntersecting) {
            visiblePagesRef.current.add(pageNumber);

            if (!thumbnailCacheRef.current[pageNumber]) {
              queueRef.current.add(pageNumber);
              addedToQueue = true;
            }
          } else {
            visiblePagesRef.current.delete(pageNumber);
            // Prioritize visible: remove non-visible from queue
            queueRef.current.delete(pageNumber);
          }
        });

        if (addedToQueue) {
          triggerProcessing();
        }
      },
      {
        root: containerRef.current,
        rootMargin: "300px", // Lookahead
        threshold: 0.01,
      },
    );

    // Observe all items
    itemsRef.current.forEach((el) => observerRef.current.observe(el));

    return () => observerRef.current?.disconnect();
  }, [totalPages, triggerProcessing]);

  // -- Retry on Chunk Load --
  useEffect(() => {
    // When loadedChunks updates, check if any visible placeholders can now be loaded
    let added = false;
    visiblePagesRef.current.forEach((page) => {
      if (
        !thumbnailCacheRef.current[page] &&
        !loadingPagesRef.current.has(page)
      ) {
        queueRef.current.add(page);
        added = true;
      }
    });

    if (added) {
      triggerProcessing();
    }
  }, [loadedChunks, triggerProcessing]);

  // -- Invalidate thumbnails only when a page's overlay signature changes --
  const prevAnnotationSignatureRef = useRef({});
  useEffect(() => {
    const previous = prevAnnotationSignatureRef.current;
    const next = annotationSignatureByPage;
    const pagesToInvalidate = new Set([
      ...Object.keys(previous),
      ...Object.keys(next),
    ]);

    let changed = false;
    pagesToInvalidate.forEach((pageKey) => {
      if (previous[pageKey] === next[pageKey]) return;
      const pageNumber = Number(pageKey);
      delete thumbnailCacheRef.current[pageNumber];
      queueRef.current.add(pageNumber);
      changed = true;
    });

    prevAnnotationSignatureRef.current = next;
    if (!changed) return;

    setThumbnails((prev) => {
      const nextThumbnails = { ...prev };
      pagesToInvalidate.forEach((pageKey) => {
        if (previous[pageKey] !== next[pageKey]) {
          delete nextThumbnails[Number(pageKey)];
        }
      });
      return nextThumbnails;
    });
    triggerProcessing();
  }, [annotationSignatureByPage, triggerProcessing]);

  // -- Ref Management --
  const setItemRef = useCallback((el, pageNum) => {
    if (el) {
      if (itemsRef.current.get(pageNum) !== el) {
        itemsRef.current.set(pageNum, el);
        observerRef.current?.observe(el);
      }
    } else {
      const existing = itemsRef.current.get(pageNum);
      if (existing) {
        observerRef.current?.unobserve(existing);
        itemsRef.current.delete(pageNum);
      }
    }
  }, []);

  // -- Auto-Scroll to Current Page (Initial Only) --
  // We only want to scroll to the current page when the sidebar first opens
  // or if the user clicks a link in the main reader that jumps far away.
  // Continual sync while scrolling can be annoying if user is exploring the sidebar independently.
  // But standard behavior is usually to keep them in sync.
  // We'll keep it simple: sync if not visible.
  useEffect(() => {
    if (!containerRef.current || !pdfDocument) return;

    const el = itemsRef.current.get(currentPage);
    if (el) {
      const rect = el.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const isVisible =
        rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

      if (!isVisible) {
        // Keep scroll changes scoped to the thumbnails container only.
        const container = containerRef.current;
        const targetTop =
          el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
        const maxScrollTop = Math.max(
          0,
          container.scrollHeight - container.clientHeight,
        );
        container.scrollTop = Math.max(0, Math.min(targetTop, maxScrollTop));
      }
    }
  }, [currentPage, pdfDocument]);

  const handleThumbnailClick = useCallback(
    (pageNumber) => {
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        if (documentType === "pdf" && typeof window !== "undefined") {
          setCurrentPage(pageNumber);
          window.dispatchEvent(
            new CustomEvent("reader:go-to-page", {
              detail: { page: pageNumber },
            }),
          );
        } else {
          setCurrentPage(pageNumber);
        }
        if (isMobile) togglePagesSidebar();
      }
    },
    [documentType, totalPages, setCurrentPage, isMobile, togglePagesSidebar],
  );

  if (documentType !== "pdf") return null;

  return (
    <div className="pages-thumbnails open" aria-label={t("pages.ariaLabel")}>
      <div className="pages-thumbnails-header">
        <h3 tabIndex="-1">{t("pages.title")}</h3>
        <span className="pages-count">
          {t("pages.count", { count: totalPages })}
        </span>
        {isMobile && (
          <button className="sidebar-close-button" onClick={togglePagesSidebar}>
            <CloseIcon width={24} height={24} />
          </button>
        )}
      </div>

      <div className="pages-thumbnails-content" ref={containerRef}>
        {totalPages > 0 ? (
          <div className="pages-thumbnails-list">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNumber) => {
                const thumbnail = thumbnails[pageNumber];
                const isLoading = loadingPages.has(pageNumber); // Use state for UI
                const isCurrentPage = pageNumber === currentPage;

                return (
                  <button
                    key={pageNumber}
                    ref={(el) => setItemRef(el, pageNumber)}
                    data-page-number={pageNumber}
                    className={`pages-thumbnail-item ${
                      isCurrentPage ? "current" : ""
                    }`}
                    onClick={() => handleThumbnailClick(pageNumber)}
                    type="button"
                  >
                    <div className="pages-thumbnail-image-container">
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={`Page ${pageNumber}`}
                          className="pages-thumbnail-image"
                          loading="lazy"
                        />
                      ) : isLoading ? (
                        <div className="pages-thumbnail-loading">
                          <div className="pages-thumbnail-spinner" />
                        </div>
                      ) : (
                        <div className="pages-thumbnail-placeholder">
                          <span>{pageNumber}</span>
                        </div>
                      )}
                    </div>
                    <div className="pages-thumbnail-label">{pageNumber}</div>
                  </button>
                );
              },
            )}
          </div>
        ) : (
          <div className="pages-thumbnails-empty">
            <p>No pages available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PagesThumbnails;
